// Custom hook encapsulating PhoneInput state, formatting, and E.164 emission.

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  AsYouType,
  parsePhoneNumberFromString,
  getCountryCallingCode,
  getCountries,
  type CountryCode,
} from 'libphonenumber-js';

/** All valid country codes from libphonenumber-js, used to guard getCountryCallingCode. */
const VALID_COUNTRIES = new Set<string>(getCountries());

/** Safe wrapper around getCountryCallingCode that returns undefined for invalid codes. */
function safeGetCallingCode(country: CountryCode, fallback: CountryCode): string {
  try {
    if (VALID_COUNTRIES.has(country)) {
      return getCountryCallingCode(country);
    }
    return getCountryCallingCode(fallback);
  } catch {
    return '1'; // Ultimate fallback to US calling code
  }
}

export interface UsePhoneInputOptions {
  value: string;
  onChange: (e164Value: string) => void;
  defaultCountry: CountryCode;
  /**
   * BUG-2132 — keep an extension typed after the number ("x12", "ext 12",
   * "#12"). It shows as " ext. 12" and is emitted as "<E.164> ext. 12". Off by
   * default: only contact phones take one (a sign-in or SMS number never does).
   */
  allowExtension?: boolean;
}

export interface UsePhoneInputReturn {
  country: CountryCode;
  displayValue: string;
  dialCode: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCountryChange: (newCountry: CountryCode) => void;
}

/** Digits, phone punctuation and an extension marker: a value the field can lay out. */
const PHONE_LIKE = /^[\d\s().+\-]*(?:(?:ext\.?|x|#)\s*\d*)?\s*$/i;

/**
 * BUG-2132 — an extension marker and its digits at the end of what was typed:
 * "x12", "ext 12", "ext. 12", "#12". The marker may be alone while the
 * operator is about to type the digits.
 */
const EXTENSION_TAIL = /\s*(?:ext\.?|x|#)\s*(\d*)\s*$/i;

/** The typed value split into the number and its extension (null when none was started). */
function splitExtension(raw: string): { main: string; ext: string | null } {
  const match = EXTENSION_TAIL.exec(raw);
  if (match == null) return { main: raw, ext: null };
  return { main: raw.slice(0, match.index), ext: match[1] };
}

/**
 * Parse an E.164 value to extract the country code and national number for display.
 * Falls back to defaultCountry if parsing fails.
 */
function parseValue(value: string, defaultCountry: CountryCode) {
  if (!value) return { country: defaultCountry, nationalNumber: '' };

  // BUG-2132 AC 5: a stored value that is not a phone number at all ("call the
  // office") shows as stored. Blanking it hid a value the row still saves and
  // publishes. Digits and phone punctuation fall through to the parse below.
  if (!PHONE_LIKE.test(value)) {
    return { country: defaultCountry, nationalNumber: value };
  }

  const parsed = parsePhoneNumberFromString(value, defaultCountry);
  if (parsed) {
    return {
      country: parsed.country ?? defaultCountry,
      nationalNumber: parsed.formatNational(),
    };
  }

  // Couldn't parse — strip non-digits and format via AsYouType
  const digits = value.replace(/\D/g, '');
  if (digits) {
    const formatter = new AsYouType(defaultCountry);
    return { country: defaultCountry, nationalNumber: formatter.input(digits) };
  }
  return { country: defaultCountry, nationalNumber: '' };
}

/**
 * Format national-significant digits for display in the (country-selected) input.
 *
 * For NANP countries the calling code "1" equals the trunk prefix, so
 * `AsYouType` treats a leading typed "1" as the country code and renders
 * "1 (234)". With a separate country selector the box holds the national number
 * only, so we lay NANP digits into the "(NXX) NXX-XXXX" template positionally —
 * "1234" -> "(123) 4" (BUG-796) — never extracting a trunk/country digit and
 * never deleting what the user typed. This matches AsYouType for normal area
 * codes (2-9) and only differs for the leading-"1" case. Non-NANP countries keep
 * AsYouType's metadata-driven formatting.
 * @param digits - national-significant digits (no punctuation)
 * @param country - the selected country
 * @param defaultCountry - fallback for the calling-code lookup
 * @returns the formatted national string for the input field
 */
function formatNationalDigits(digits: string, country: CountryCode, defaultCountry: CountryCode): string {
  if (safeGetCallingCode(country, defaultCountry) !== '1') {
    return new AsYouType(country).input(digits);
  }
  if (digits.length < 3) return digits;
  const area = digits.slice(0, 3);
  if (digits.length === 3) return `(${area})`;
  const rest = digits.slice(3);
  const mid = rest.slice(0, 3);
  const end = rest.slice(3, 7);
  const overflow = rest.slice(7);
  return `(${area}) ${mid}${end ? `-${end}` : ''}${overflow}`;
}

/** Result of deriving the next phone-input state from a raw <input> value. */
interface InputChangeResult {
  /** Country to adopt (e.g. detected from a pasted international number), or null to keep the current one. */
  country: CountryCode | null;
  /** National-number string to show in the field. */
  displayValue: string;
  /** E.164 value to emit (empty string when cleared). */
  emit: string;
}

/**
 * Pure derivation for a phone <input> change. Handles pasted international
 * numbers, backspace-of-a-formatting-char compensation (BUG-732), and
 * as-you-type national formatting. No React, no side effects — the hook applies
 * the returned state. Mirrors the prior inline logic exactly, including the
 * paste-then-fall-through case where the country is adopted but the display /
 * emit are still computed against the (not-yet-updated) current country.
 * @param opts - raw input value, current country, default country, previous display value
 * @returns the next country (or null), display string, and E.164 value to emit
 */
function resolveInputChange(opts: {
  raw: string;
  country: CountryCode;
  defaultCountry: CountryCode;
  prevDisplay: string;
}): InputChangeResult {
  const { raw, country, defaultCountry, prevDisplay } = opts;
  let nextCountry: CountryCode | null = null;

  // Detect pasted international number (starts with +)
  if (raw.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(raw, country);
    if (parsed) {
      const detectedCountry = parsed.country ?? country;
      nextCountry = detectedCountry; // adopt the detected country whenever the paste parses
      if (parsed.isValid()) {
        return { country: nextCountry, displayValue: parsed.formatNational(), emit: parsed.format('E.164') };
      }
      // Partial international number — detect country but format remaining digits
      const nationalDigits = parsed.nationalNumber as string;
      if (nationalDigits) {
        const formatter = new AsYouType(detectedCountry);
        return {
          country: nextCountry,
          displayValue: formatter.input(nationalDigits),
          emit: `+${safeGetCallingCode(detectedCountry, defaultCountry)}${nationalDigits}`,
        };
      }
      // else: fall through to digit handling (display/emit use the current country)
    }
  }

  let digits = raw.replace(/\D/g, '');

  // As-you-type formatting re-inserts punctuation (e.g. "201" -> "(201)") on
  // every keystroke. When the user backspaces a formatting char, the digit
  // count is unchanged, and a naive reformat re-adds the punctuation — trapping
  // the caret so the area code can never be deleted (BUG-732). Detect that exact
  // case (the field got shorter but no digit was removed) and drop the trailing
  // digit instead, so Backspace makes real progress.
  const prevDigits = prevDisplay.replace(/\D/g, '');
  if (raw.length < prevDisplay.length && digits.length === prevDigits.length && digits.length > 0) {
    digits = digits.slice(0, -1);
  }

  if (!digits) {
    return { country: nextCountry, displayValue: '', emit: '' };
  }

  // BUG-2132 AC 5: a full NANP number typed or pasted with its trunk "1"
  // (1-218-555-0142) is the number itself. Below 11 digits the layout stays
  // positional (BUG-796), so a typed leading 1 is never deleted mid-entry.
  if (digits.length === 11 && digits.startsWith('1') && safeGetCallingCode(country, defaultCountry) === '1') {
    digits = digits.slice(1);
  }

  const formatted = formatNationalDigits(digits, country, defaultCountry);
  const fullNumber = `+${safeGetCallingCode(country, defaultCountry)}${digits}`;
  const parsed = parsePhoneNumberFromString(fullNumber, country);
  return { country: nextCountry, displayValue: formatted, emit: parsed ? parsed.format('E.164') : fullNumber };
}

/**
 * {@link resolveInputChange} with an extension kept off the end (BUG-2132 AC 1).
 *
 * The number before the marker runs through the usual derivation; the
 * extension's digits ride after it, shown as " ext. 12" and emitted as
 * "<E.164> ext. 12". A Backspace that removes only marker text, not a digit,
 * drops one extension digit, or the marker itself once the extension is empty,
 * so the re-rendered " ext. " can never trap the caret (the BUG-732 shape).
 * @param opts - as {@link resolveInputChange}
 * @returns the next country, display and emit
 */
function resolveInputChangeWithExtension(opts: {
  raw: string;
  country: CountryCode;
  defaultCountry: CountryCode;
  prevDisplay: string;
}): InputChangeResult {
  const typed = splitExtension(opts.raw);
  const prev = splitExtension(opts.prevDisplay);
  if (typed.ext == null) {
    return resolveInputChange({ ...opts, prevDisplay: prev.main });
  }

  let ext = typed.ext;
  // Only a deletion that left the number's own digits alone removed marker text:
  // deleting a digit of the number shortens the value too, and must not cost
  // the extension a digit.
  const digitsOf = (text: string) => text.replace(/\D/g, '');
  const markerOnly =
    opts.raw.length < opts.prevDisplay.length && digitsOf(typed.main) === digitsOf(prev.main);
  if (markerOnly && prev.ext != null && ext === prev.ext) {
    if (ext === '') {
      return resolveInputChange({ ...opts, raw: typed.main, prevDisplay: prev.main });
    }
    ext = ext.slice(0, -1);
  }

  const main = resolveInputChange({ ...opts, raw: typed.main, prevDisplay: prev.main });
  if (main.emit === '') return main;
  return {
    country: main.country,
    displayValue: `${main.displayValue} ext. ${ext}`,
    emit: ext === '' ? main.emit : `${main.emit} ext. ${ext}`,
  };
}

/**
 * Encapsulates phone input state: country selection, display formatting, and E.164 emission.
 * Handles paste of international numbers, external value sync, and country switching.
 * @param options - value, onChange, defaultCountry
 * @returns State and handlers for the PhoneInput component
 */
export function usePhoneInput({
  value,
  onChange,
  defaultCountry,
  allowExtension = false,
}: UsePhoneInputOptions): UsePhoneInputReturn {
  // Parsed once, on mount only — external changes are handled by the useEffect
  // below, not by re-deriving here. A lazy useState initializer expresses
  // "compute once" without a dependency array (and so without the exhaustive-deps
  // suppression a mount-only useMemo would need; packages/ui does not register
  // the react-hooks plugin).
  const [initial] = useState(() => parseValue(value, defaultCountry));
  const [country, setCountry] = useState<CountryCode>(initial.country);
  const [displayValue, setDisplayValue] = useState(initial.nationalNumber);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track the last value we emitted to skip redundant re-parse on self-initiated changes
  const lastEmittedRef = useRef(value);

  // Mirror of the latest displayValue, read synchronously inside
  // handleInputChange to detect a Backspace that removed only a formatting
  // character (BUG-732) — without re-creating the callback on every keystroke.
  const displayValueRef = useRef(displayValue);
  useEffect(() => {
    displayValueRef.current = displayValue;
  }, [displayValue]);

  // Sync from external value changes (form reset, pre-fill) — skip self-initiated changes
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const { country: parsedCountry, nationalNumber } = parseValue(value, defaultCountry);
    setCountry(parsedCountry);
    setDisplayValue(nationalNumber);
    lastEmittedRef.current = value;
  }, [value, defaultCountry]);

  const dialCode = `+${safeGetCallingCode(country, defaultCountry)}`;

  /** Format digits as the user types and emit E.164. Handles paste of international numbers. */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const resolve = allowExtension ? resolveInputChangeWithExtension : resolveInputChange;
      const { country: nextCountry, displayValue: nextDisplay, emit } = resolve({
        raw: e.target.value,
        country,
        defaultCountry,
        prevDisplay: displayValueRef.current,
      });
      if (nextCountry) setCountry(nextCountry);
      setDisplayValue(nextDisplay);
      lastEmittedRef.current = emit;
      onChange(emit);
    },
    [country, defaultCountry, onChange, allowExtension],
  );

  /** When user picks a new country, re-parse existing digits with the new code. */
  const handleCountryChange = useCallback(
    (newCountry: CountryCode) => {
      setCountry(newCountry);
      inputRef.current?.focus();

      // BUG-2132: an extension is not part of the number being re-read.
      const { main, ext } = allowExtension ? splitExtension(displayValue) : { main: displayValue, ext: null };
      const extSuffix = ext ? ` ext. ${ext}` : '';
      const digits = main.replace(/\D/g, '');
      if (!digits) {
        lastEmittedRef.current = '';
        onChange('');
        return;
      }

      const fullNumber = `+${safeGetCallingCode(newCountry, defaultCountry)}${digits}`;
      const parsed = parsePhoneNumberFromString(fullNumber, newCountry);
      if (parsed) {
        const e164 = `${parsed.format('E.164')}${extSuffix}`;
        setDisplayValue(`${parsed.formatNational()}${extSuffix}`);
        lastEmittedRef.current = e164;
        onChange(e164);
      } else {
        const formatter = new AsYouType(newCountry);
        setDisplayValue(`${formatter.input(digits)}${extSuffix}`);
        lastEmittedRef.current = `${fullNumber}${extSuffix}`;
        onChange(`${fullNumber}${extSuffix}`);
      }
    },
    [displayValue, defaultCountry, onChange, allowExtension],
  );

  return { country, displayValue, dialCode, inputRef, handleInputChange, handleCountryChange };
}
